/**
 * Η φόρμα «Κρατικά τέλη» της διαχείρισης προϊόντων (ProductManagement) ως καθαρή συνάρτηση.
 * Φ5 της κατάργησης της τιμής προϊόντος (docs/base_price_phase5_plan.md §3, απόφαση Ε8α).
 *
 * ΓΙΑΤΙ ΥΠΑΡΧΕΙ: μετά το κλείδωμα της Φ4 η τιμή ζει ΜΟΝΟ στο μέγεθος και το ποσό ενός τέλους
 * ΜΟΝΟ στο state_fees. Η μετάβαση από/προς τέλος είναι το ένα σημείο όπου η φόρμα πρέπει να
 * αλλάξει τα μεγέθη μόνη της, και οι κανόνες της πρέπει να συμφωνούν με τον backend
 * (priceWriteGuard: τέλος χωρίς μεγέθη, μη-τέλος με ≥1 μέγεθος), αλλιώς η αποθήκευση κόβεται.
 *
 * Κανόνες:
 *  - το ποσό του τέλους είναι του SERVER (GET /fiscal/state-fees → unit_gross)· εδώ μόνο
 *    εμφανίζεται, δεν υπολογίζεται ΦΠΑ και δεν γράφεται πουθενά στο προϊόν (Ε8α)·
 *  - τέλος ΔΕΝ έχει μεγέθη: σε υπάρχον μη-τέλος με μεγέθη η μετατροπή απαγορεύεται (ο
 *    χειριστής φτιάχνει νέο προϊόν-τέλος), σε νέο τα μεγέθη αδειάζουν·
 *  - φεύγοντας από τέλος το προϊόν χρειάζεται μέγεθος με τιμή. Υπάρχον τέλος κρατά την τωρινή
 *    του τιμή (F1-W3-7): το ποσό του state_fees μπαίνει στο «Κανονικό» (ή στο προεπιλεγμένο του
 *    μέγεθος, που ξαναγίνεται πωλήσιμο — sizesLeavingFee). Αλλιώς από μια απλή αλλαγή radio θα
 *    γινόταν €0 ή θα εξαφανιζόταν από το ταμείο.
 */

import { isFeeProduct } from './pricing';

export type FeeMode = 'none' | 'fee' | 'trigger';

/** Γραμμή του GET /api/fiscal/state-fees όπως την κρατά η φόρμα. */
export type StateFeeRow = {
  code: number;
  title?: string;
  short_label?: string;
  /** καθαρό ποσό (χωρίς ΦΠΑ)· μόνο για την ετικέτα «€NET + ΦΠΑ» όταν λείπει το unit_gross */
  unit_price?: number | string | null;
  /** μικτό ανά τεμάχιο, string NUMERIC από τον server (π.χ. '0.05') */
  unit_gross?: number | string | null;
};

type SizeLike = {
  name: string;
  price_modifier: number;
  is_default: boolean;
  is_available: boolean;
  is_hidden?: boolean;
  price_modifier_take_away?: number | null;
  price_modifier_delivery?: number | null;
  price_modifier_dine_in?: number | null;
};

const SIZE_PRICE_FIELDS = ['price_modifier', 'price_modifier_take_away', 'price_modifier_delivery', 'price_modifier_dine_in'] as const;

/**
 * Αν κάποιο μέγεθος κρατά τιμή (βασική ή ανά κανάλι ≠ 0). Όλα τα μεγέθη μετράνε, και τα
 * κρυμμένα: η ερώτηση είναι «πού ζει η τιμή», όχι «τι πουλιέται».
 */
export function sizesCarryPrice(sizes: SizeLike[]): boolean {
  return sizes.some(sz => SIZE_PRICE_FIELDS.some(k => {
    const v = sz[k];
    return v !== null && v !== undefined && Number(v) !== 0 && Number.isFinite(Number(v));
  }));
}

export type FeeFormLike = {
  vat: number;
  sizes: SizeLike[];
  max_discount_percent?: number;
  fee_category?: number | null;
  auto_plastic_fee_count?: number;
};

export type FeeModeOptions = {
  /** νέο προϊόν (δεν υπάρχει editingProduct) */
  isNew: boolean;
  /** πόσα μεγέθη είχε το προϊόν ΟΠΩΣ ΑΠΟΘΗΚΕΥΤΗΚΕ (όλα, και τα κρυμμένα/μη διαθέσιμα) */
  originalSizeCount: number;
  /**
   * fee_category του προϊόντος όπως αποθηκεύτηκε. Υπάρχον τέλος που ΗΔΗ έχει μεγέθη (σχήμα
   * 7700) δεν μπλοκάρεται όταν αλλάζει κωδικό 8↔16 — η φραγή αφορά το «γίνεται τέλος».
   */
  originalFeeCategory?: number | null;
  /**
   * Τα μεγέθη του προϊόντος ΟΠΩΣ ΑΠΟΘΗΚΕΥΤΗΚΑΝ. Τέλος που φεύγει και ξαναγυρνά σε «τέλος» πριν
   * την αποθήκευση τα ξαναπαίρνει: ο backend δέχεται σε υπάρχον τέλος μόνο τα ίδια μεγέθη με
   * τις ίδιες τιμές (αλλιώς 400 FEE_NO_SIZES).
   */
  originalSizes?: SizeLike[];
  /**
   * fee_unit_gross του αποθηκευμένου τέλους (GET /stores/:id/products): η τιμή που χρεώνει σήμερα
   * το ταμείο. Προηγείται του state_fees της φόρμας, που ίσως δεν φορτώθηκε ή που ο χειριστής
   * άλλαξε κωδικό πριν φύγει από το τέλος.
   */
  originalFeeGross?: number | string | null;
  stateFees: StateFeeRow[];
};

export type FeeModeResult<F> = { form: F; blocked?: undefined } | { form: F; blocked: 'has_sizes' };

/** Το «Κανονικό» που ξεκινά κάθε νέο προϊόν (ίδιο με το resetProductForm). */
const PLACEHOLDER_SIZE: SizeLike = { name: 'Κανονικό', price_modifier: 0, is_default: true, is_available: true };

/**
 * «Είναι τέλος» = ο ΙΔΙΟΣ ορισμός με το ταμείο (pricing.ts, §3: fee_category != null), ώστε
 * φόρμα και ταμείο να μη διαφωνούν ποτέ για το τι είναι τέλος.
 */
export function isFeeCategory(v: number | null | undefined): boolean {
  return isFeeProduct({ fee_category: v });
}

/** Έγκυρος κωδικός για το select του τέλους (8 σακούλα / 16 πλαστικά)· μόνο για την επιλογή. */
function isSelectableFeeCode(v: number | null | undefined): v is number {
  return v === 8 || v === 16;
}

/** Η τρέχουσα επιλογή του radio, από τη φόρμα. */
export function feeModeOf(form: Pick<FeeFormLike, 'fee_category' | 'auto_plastic_fee_count'>): FeeMode {
  if (isFeeCategory(form.fee_category)) return 'fee';
  return (form.auto_plastic_fee_count ?? 0) > 0 ? 'trigger' : 'none';
}

/** Μικτό ποσό τέλους ανά τεμάχιο από τον server (θετικός αριθμός) ή null αν λείπει. */
export function feeUnitGrossOf(stateFees: StateFeeRow[], code: number): number | null {
  const row = stateFees.find(f => Number(f.code) === code);
  if (!row || row.unit_gross === null || row.unit_gross === undefined || row.unit_gross === '') return null;
  const n = Number(row.unit_gross);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Κείμενο ποσού για ετικέτα/ενημερωτικό: '€0.05' από το unit_gross· αν λείπει,
 * '€0.04 + ΦΠΑ' από το καθαρό — ΧΩΡΙΣ να κάνουμε εμείς τον πολλαπλασιασμό.
 */
export function feeAmountText(stateFees: StateFeeRow[], code: number, vatLabel = 'ΦΠΑ'): string | null {
  const gross = feeUnitGrossOf(stateFees, code);
  if (gross !== null) return `€${gross.toFixed(2)}`;
  const row = stateFees.find(f => Number(f.code) === code);
  const net = row ? Number(row.unit_price) : NaN;
  if (row && row.unit_price !== null && row.unit_price !== undefined && Number.isFinite(net)) {
    return `€${net.toFixed(2)} + ${vatLabel}`;
  }
  return null;
}

/** Αν η μετάβαση σε «τέλος» απαγορεύεται: υπάρχον ΜΗ-τέλος που έχει αποθηκευμένα μεγέθη. */
export function feeModeBlocked(opts: Pick<FeeModeOptions, 'isNew' | 'originalSizeCount' | 'originalFeeCategory'>): boolean {
  return !opts.isNew && opts.originalSizeCount > 0 && !isFeeCategory(opts.originalFeeCategory ?? null);
}

/** Πωλήσιμο στο ταμείο: ίδιος κανόνας με το SELLABLE_SIZE του server. */
const isSellable = (s: SizeLike) => s.is_available !== false && s.is_hidden !== true;

/** Θετικός αριθμός ή null (string NUMERIC του server, '0.00', κενό). */
function positiveAmount(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Μεγέθη για τέλος που γίνεται προϊόν. Τέλος που δεν αποθηκεύτηκε ποτέ ως τέλος (νέο, ή μη-τέλος
 * που πέρασε για λίγο από το radio): το «Κανονικό 0» του — δεν χρεώθηκε ποτέ ως τέλος.
 * Αποθηκευμένο τέλος: κρατά την τιμή που χρεώνει σήμερα, το ποσό του state_fees (F1-W3-7) —
 *  - χωρίς μεγέθη → «Κανονικό» σε αυτό το ποσό·
 *  - χωρίς ΠΩΛΗΣΙΜΟ μέγεθος (μετά την πύλη της Φ5 κάθε μέγεθος τέλους είναι αποσυρμένο/κρυφό,
 *    fee_with_sellable_sizes = 0) → το προεπιλεγμένο του (ή το πρώτο) ξαναγίνεται πωλήσιμο στο
 *    ποσό του τέλους, χωρίς τιμές καναλιού. Αλλιώς το προϊόν θα έφευγε από το ταμείο (ορατό =
 *    ≥1 πωλήσιμο μέγεθος) ή θα κρατούσε την παλιά τιμή του αποσυρμένου μεγέθους. Νέο μέγεθος
 *    «Κανονικό» δεν γίνεται: το όνομα το κρατά συνήθως το ίδιο το αποσυρμένο (UNIQUE ονόματος)·
 *  - με πωλήσιμα μεγέθη (δεδομένα πριν την πύλη): αν κανένα δεν κρατά τιμή (σχήμα 372/373:
 *    «Regular» 0), το ποσό μπαίνει στο προεπιλεγμένο· αν κρατούν, μένουν όπως είναι.
 * Χωρίς ποσό από τον server η τιμή είναι 0 — όσο χρεώνει και το ταμείο για τέλος χωρίς ποσό
 * (feeUnitGross → 0) — και ο χειριστής τη βλέπει στη φόρμα πριν αποθηκεύσει.
 */
function sizesLeavingFee<S extends SizeLike>(sizes: S[], gross: number | null, keepsPrice: boolean): S[] {
  if (sizes.length === 0) {
    return [{ ...PLACEHOLDER_SIZE, price_modifier: keepsPrice ? (gross ?? 0) : 0 } as S];
  }
  if (!keepsPrice) return sizes;
  if (!sizes.some(isSellable)) {
    const idx = Math.max(0, sizes.findIndex(s => s.is_default));
    return sizes.map((s, i) => (i === idx
      ? {
        ...s, price_modifier: gross ?? 0, is_available: true, is_hidden: false,
        price_modifier_take_away: null, price_modifier_delivery: null, price_modifier_dine_in: null,
      }
      : s));
  }
  if (gross === null || sizesCarryPrice(sizes)) return sizes;
  // Το ταμείο τιμολογεί το προεπιλεγμένο ΠΩΛΗΣΙΜΟ (αλλιώς το πρώτο πωλήσιμο).
  const sellableDefault = sizes.findIndex(s => s.is_default && isSellable(s));
  const idx = sellableDefault >= 0 ? sellableDefault : sizes.findIndex(isSellable);
  return sizes.map((s, i) => (i === idx ? { ...s, price_modifier: gross } : s));
}

/**
 * Εφαρμόζει μια επιλογή του radio (ή αλλαγή κωδικού στο select, mode 'fee' με άλλο code).
 * Επιστρέφει νέο αντικείμενο φόρμας· σε φραγή επιστρέφει την ΙΔΙΑ φόρμα με blocked.
 */
export function applyFeeMode<F extends FeeFormLike>(
  form: F,
  mode: FeeMode,
  code: number | null,
  opts: FeeModeOptions,
): FeeModeResult<F> {
  const wasFee = isFeeCategory(form.fee_category);

  if (mode === 'fee') {
    if (feeModeBlocked(opts)) return { form, blocked: 'has_sizes' };
    const feeCode = isSelectableFeeCode(code) ? code : (isSelectableFeeCode(form.fee_category) ? form.fee_category : 8);
    // Νέο, ή υπάρχον χωρίς αποθηκευμένα μεγέθη: ό,τι μέγεθος υπάρχει στη φόρμα (το «Κανονικό 0»
    // του νέου) είναι μη αποθηκευμένο και πετιέται. Υπάρχον τέλος που ήδη έχει μεγέθη: τα
    // αφήνουμε ως έχουν — το PUT του καταστήματος αγνοεί το sizes:[] (αγγίζει μεγέθη μόνο με
    // length > 0) και η φόρμα θα έδειχνε «χωρίς μεγέθη» ενώ η βάση θα τα κρατούσε.
    // Αν η φόρμα είχε φύγει από το τέλος, τα μεγέθη της άλλαξαν (sizesLeavingFee): ξαναπαίρνει
    // τα αποθηκευμένα.
    const sizes = opts.isNew || opts.originalSizeCount === 0
      ? []
      : (isFeeCategory(opts.originalFeeCategory ?? null) && opts.originalSizes
        ? (opts.originalSizes as F['sizes'])
        : form.sizes);
    return {
      form: {
        ...form,
        fee_category: feeCode,
        auto_plastic_fee_count: 0,
        vat: 24,
        // Τα κρατικά τέλη περνούν αυτούσια· καμία έκπτωση δεν τα αγγίζει.
        max_discount_percent: 0,
        sizes,
      },
    };
  }

  const next: F = {
    ...form,
    fee_category: null,
    auto_plastic_fee_count: mode === 'trigger'
      ? ((form.auto_plastic_fee_count ?? 0) > 0 ? (form.auto_plastic_fee_count as number) : 1)
      : 0,
  };
  if (wasFee) {
    // «Κρατά την τιμή του» μόνο ό,τι ΑΠΟΘΗΚΕΥΤΗΚΕ ως τέλος: ένα μη-τέλος που πέρασε για λίγο από
    // το radio δεν χρεώθηκε ποτέ το ποσό του τέλους.
    const keepsPrice = !opts.isNew && isFeeCategory(opts.originalFeeCategory ?? null);
    const gross = keepsPrice
      ? (positiveAmount(opts.originalFeeGross)
        ?? feeUnitGrossOf(opts.stateFees, Number(opts.originalFeeCategory)))
      : null;
    next.sizes = sizesLeavingFee(form.sizes, gross, keepsPrice) as F['sizes'];
  }
  return { form: next };
}
